import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PaywallButton } from "@/components/PaywallButton";
import { CreatorCodeLink } from "@/components/monetization/CreatorCodeLink";
import { Lock } from "lucide-react";
import { hasActivePremiumAccess } from "@/lib/premiumStatus";

/**
 * Shows a one-time modal when a user's 7-day Pro free trial has ended
 * and they have not upgraded. Offers an upgrade CTA (native paywall) or
 * dismissal to continue with limited access.
 */
export const TrialEndedModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const check = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("is_premium, bypass_rate_limit, trial_ends_at")
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled || !data) return;

      const trialEndsAt = (data as any).trial_ends_at as string | null;
      if (!trialEndsAt) return;

      const endsMs = new Date(trialEndsAt).getTime();
      if (!Number.isFinite(endsMs) || endsMs > Date.now()) return;

      // Still has access (premium or bypass) — don't show.
      if (hasActivePremiumAccess(data)) return;

      const dismissKey = `trial_ended_shown_${user.id}_${endsMs}`;
      if (localStorage.getItem(dismissKey) === "1") return;

      localStorage.setItem(dismissKey, "1");
      setOpen(true);
    };

    void check();

    const handler = () => { void check(); };
    window.addEventListener("premium-status-changed", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("premium-status-changed", handler);
    };
  }, [user?.id]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-[0_0_30px_hsl(var(--primary)_/_0.5)]">
            <Lock className="w-7 h-7 text-primary-foreground" />
          </div>
          <DialogTitle className="text-center text-2xl">
            Your free Pro trial has ended
          </DialogTitle>
          <DialogDescription className="text-center">
            Your account stays open — you can keep viewing all of your past
            workouts, nutrition, and progress data forever. Upgrade any time to
            unlock logging new workouts, AI generation, and other Pro features.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 mt-2">
          <PaywallButton
            externalId={user?.id || ""}
            offering="lovable_app"
            variant="glow"
            size="lg"
            className="w-full"
            feature="trial_ended_modal"
            onComplete={() => setOpen(false)}
          >
            Start Free Trial
          </PaywallButton>
          <div className="flex justify-center">
            <CreatorCodeLink />
          </div>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => setOpen(false)}
          >
            Keep viewing my data
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
